// Thang sức mạnh (POWER_SCALE.md §2–§3): 9 cảnh giới, tiểu cảnh, thọ nguyên, Giai, bậc L, Power Index.
// Đây là nơi DUY NHẤT biết có bao nhiêu cảnh giới và mỗi cảnh giới có mấy tiểu cảnh.
// Sim chỉ lưu (realm, s); Giai, L và P luôn tính lại.
import { BALANCE } from './balance';

export const REALM_IDS = [
  'qiRefining',
  'foundation',
  'goldenCore',
  'nascentSoul',
  'deityTransformation',
  'voidRefining',
  'bodyIntegration',
  'mahayana',
  'tribulation',
] as const;
export type RealmId = (typeof REALM_IDS)[number];

export const LAYER_SUB_STAGES = [
  'layer1',
  'layer2',
  'layer3',
  'layer4',
  'layer5',
  'layer6',
  'layer7',
  'layer8',
  'layer9',
] as const;
export const MAJOR_SUB_STAGES = ['early', 'middle', 'late', 'peak'] as const;
export type SubStageId = (typeof LAYER_SUB_STAGES)[number] | (typeof MAJOR_SUB_STAGES)[number];

export interface RealmDef {
  /** 1..9 (LK = 1). */
  readonly realm: number;
  readonly id: RealmId;
  readonly subStages: readonly SubStageId[];
  /** Thọ nguyên tối đa (năm game). */
  readonly lifespanYears: number;
}

export const REALMS: readonly RealmDef[] = REALM_IDS.map((id, i) => ({
  realm: i + 1,
  id,
  // Luyện Khí chia 9 tầng; từ Trúc Cơ trở đi 4 tiểu cảnh (POWER_SCALE §2.2).
  subStages: i === 0 ? LAYER_SUB_STAGES : MAJOR_SUB_STAGES,
  lifespanYears: lifespanOf(i),
}));

function lifespanOf(realmIdx: number): number {
  const v = BALANCE.LIFESPAN_BY_REALM[realmIdx];
  if (v === undefined) throw new Error(`missing LIFESPAN_BY_REALM[${realmIdx}]`);
  return v;
}

export const REALM_COUNT = REALMS.length;
export const FIRST_REALM = 1;
export const LAST_REALM = REALM_COUNT;

export function realmDef(realm: number): RealmDef {
  const d = REALMS[realm - 1];
  if (!d) throw new RangeError(`realm out of range 1..${REALM_COUNT}: ${realm}`);
  return d;
}

export function subStageCount(realm: number): number {
  return realmDef(realm).subStages.length;
}

function assertStage(realm: number, s: number): void {
  const n = subStageCount(realm);
  if (!Number.isInteger(s) || s < 1 || s > n) throw new RangeError(`sub-stage out of range 1..${n}: ${s}`);
}

/**
 * Power Index P (POWER_SCALE §3.2). Mỗi đại cảnh giới đúng ×10, chia đều cho số tiểu cảnh
 * của nó: LK tầng t = 10^(1 + (t−1)/9), từ TC = 10^(realm + (s−1)/4).
 */
export function powerIndex(realm: number, s: number): number {
  assertStage(realm, s);
  return Math.pow(10, realm + (s - 1) / subStageCount(realm));
}

/** Giai = realm × 10 + s (LK7 = 17, ĐK ĐVM = 94). */
export function giai(realm: number, s: number): number {
  assertStage(realm, s);
  return realm * 10 + s;
}

/** Bậc tuyến tính L = 1..41: đếm mọi tiểu cảnh liên tiếp (POWER_SCALE §3.1). */
export function stageL(realm: number, s: number): number {
  assertStage(realm, s);
  let l = 0;
  for (let r = FIRST_REALM; r < realm; r++) l += subStageCount(r);
  return l + s;
}

/** Tổng số bậc L (41). */
export const MAX_STAGE_L = REALMS.reduce((n, d) => n + d.subStages.length, 0);

/** Ngược của stageL. */
export function fromStageL(l: number): { realm: number; s: number } {
  if (!Number.isInteger(l) || l < 1 || l > MAX_STAGE_L)
    throw new RangeError(`L out of range 1..${MAX_STAGE_L}: ${l}`);
  let rest = l;
  for (const d of REALMS) {
    if (rest <= d.subStages.length) return { realm: d.realm, s: rest };
    rest -= d.subStages.length;
  }
  throw new RangeError(`invalid L: ${l}`);
}

export function subStageId(realm: number, s: number): SubStageId {
  assertStage(realm, s);
  const id = realmDef(realm).subStages[s - 1];
  if (!id) throw new RangeError(`invalid sub-stage: ${realm}/${s}`);
  return id;
}
